import './ToastManager.scss';
import DIContainer from '@components/DependencyInjectionContainer.js';
import { createElement, getID } from '@components/Utility/index.js';
import { Container } from '@components/Component/Container/Container.js';
import { DateTimeManager } from '@components/DateTimeManager/DateTimeManager.js';
/*
success popup
DIContainer.getInstance<ToastManagerInterface>('ToastManager').toast({
    message: this.CaptionManager.getCaption('system_cachecleared')
    ,subject: this.CaptionManager.getCaption('system_success')
    ,style: 'success' // (success/warning/error, optional)
});
caption key based popup
DIContainer.getInstance<ToastManagerInterface>('ToastManager').toast({
    captionKey: 'some_key' // caption type "title" will be the subject, "caption" will be the message.
// you can also mix captionKey with above format, if you just need a generic "Success" subject + a message coming from a caption key.
});
no auto close
DIContainer.getInstance<ToastManagerInterface>('ToastManager').toast({
    message: 'hi'
    ,requireInteraction: true // doesnt auto close, user must click X
    ,onDismiss: () => {// do something}
});
click handler
DIContainer.getInstance<ToastManagerInterface>('ToastManager').toast({
   message: 'click me'
   ,onClick: () => {//do something}
});
inside a form
DIContainer.getInstance<ToastManagerInterface>('ToastManager').toast({
    message: 'hi'
    ,scope: form.globalId
});
changing message of an existing toast (eg. showing "request is loading" > "request completed"
DIContainer.getInstance<ToastManagerInterface>('ToastManager').toast({
   message: 'Request pending'
   ,dedupeKey: 'request123'
});

DIContainer.getInstance<ToastManagerInterface>('ToastManager').toast({
   message: 'Request completed'
   ,dedupeKey: 'request123'
});
programmatically closing it
const toast = DIContainer.getInstance<ToastManagerInterface>('ToastManager').toast({
   message: 'Loading Data...'
   ,closable: false
   ,durationMs: 0 // disable auto hiding
});

// after some loading finished
toast.dismiss();
* */
export class ToastManager extends Container {
    constructor() {
        super(...arguments);
        this.componentName = 'ToastManager';
        this.rootElementType = 'div';
        this.stacks = new Map();
        this.maxVisibleApp = 3;
        this.maxVisibleComponent = 3;
    }
    draw() {
        super.draw();
        this.registerToastStack('app', this.rootElement.querySelector('.s360ToastStack'));
        // pause timers if tab is hidden
        this.registerEvent('visibilitychange', document, () => {
            const hidden = document.hidden;
            for (const s of this.stacks.values()) {
                for (const t of s.list)
                    hidden ? this.pauseTimer(t) : this.resumeTimer(t);
            }
        });
    }
    registerToastStack(key, container) {
        container.classList.add('s360ToastStack', `s360ToastStack--${key === 'app' ? 'app' : 'component'}`);
        container.dataset.stackKey = key;
        this.stacks.set(key, { container, list: [], queue: [] });
    }
    getToastStack(key) {
        return this.stacks.get(key)?.container;
    }
    unregisterToastStack(key) {
        this.stacks.get(key)?.list.forEach((t) => {
            t.toast.destroy();
        });
        this.stacks.get(key)?.queue.forEach((t) => {
            t.toast.destroy();
        });
        this.stacks.delete(key);
    }
    success(message, opts = {}) { return this.toast({ ...opts, message, style: 'success' }); }
    info(message, opts = {}) { return this.toast({ ...opts, message, style: 'info' }); }
    warning(message, opts = {}) { return this.toast({ ...opts, message, style: 'warning' }); }
    error(message, opts = {}) { return this.toast({ ...opts, message, style: 'error' }); }
    toast(opts) {
        const id = getID('toast');
        const style = opts.style ?? 'info';
        const scope = opts.scope ?? 'app';
        const closable = opts.closable ?? true;
        const key = this.stackKey(scope);
        const stack = this.stacks.get(key) || this.stacks.get('app');
        // Dedupe within stack
        if (opts.dedupeKey) {
            const dup = stack.list.find(t => t.opts.dedupeKey === opts.dedupeKey)
                ?? stack.queue.find(t => t.opts.dedupeKey === opts.dedupeKey);
            if (dup) {
                // Update content & refresh timer
                dup.opts = { ...dup.opts, ...opts, style };
                this.updateToast(dup);
                this.resetTimer(dup);
                dup.createdAt = Date.now();
                this.reflowStack(key);
                return { toast: dup.toast, dismiss: () => this.dismiss(dup.id, 'programmatic') };
            }
        }
        // Create Toast component
        const el = createElement('div', [`s360Toast--${style}`]);
        opts.data = opts.data || {
            notificationid: '',
            type: 'POPUP',
            linkno: '',
            linktype: '',
            subject: '',
            message: '',
            xmlparams: '',
            timestamp: DateTimeManager.now('USER', 'string')
        };
        if (opts.message) {
            opts.data.message = opts.message;
        }
        else {
            opts.message = opts.data.message;
        }
        if (opts.subject) {
            opts.data.subject = opts.subject;
        }
        else {
            opts.subject = opts.data.subject;
        }
        const toast = DIContainer.new('Toast', id, el, { data: {
                ...opts.data
            } });
        if (!closable) {
            toast.buttonHiddenSet('dismiss', true);
        }
        toast.style = opts.style || 'info';
        const internal = {
            id,
            opts: { ...opts },
            createdAt: Date.now(),
            remainingMs: (opts.requireInteraction ? 0 : (opts.durationMs ?? 3500)) ?? 3500,
            toast,
            el
        };
        this.componentAdd(toast, false);
        // Attach to stack
        const max = this.isAppKey(key) ? this.maxVisibleApp : this.maxVisibleComponent;
        if (opts.captionKey) {
            this.CaptionManager.onLoad$(opts.captionKey, true)
                .subscribe(() => {
                if (this.CaptionManager.getCaption(opts.captionKey + '_title')) {
                    toast.subject = this.CaptionManager.getCaption(opts.captionKey + '_title');
                }
                if (this.CaptionManager.getCaption(opts.captionKey + '_caption')) {
                    toast.message = this.CaptionManager.getCaption(opts.captionKey + '_caption');
                }
                if (stack.list.length < max) {
                    stack.list.unshift(internal);
                    stack.container.appendChild(el);
                    this.renderToast(internal);
                }
                else {
                    stack.queue.push(internal);
                }
                this.reflowStack(key);
            });
        }
        else {
            if (stack.list.length < max) {
                stack.list.unshift(internal);
                stack.container.appendChild(el);
                this.renderToast(internal);
            }
            else {
                stack.queue.push(internal);
            }
            this.reflowStack(key);
        }
        return { toast, dismiss: () => this.dismiss(id, 'programmatic') };
    }
    renderToast(internal) {
        if (!internal.toast.drawn) {
            internal.toast.draw();
            internal.toast.registerEvent('mouseenter', internal.el, () => this.pauseTimer(internal));
            internal.toast.registerEvent('mouseleave', internal.el, () => this.resumeTimer(internal));
            internal.toast.registerEvent('click', internal.el, (event) => {
                if (!event.target.closest('button')) {
                    // they didnt click on the exit button.
                    if (internal.opts.onClick) {
                        internal.opts.onClick();
                        this.dismiss(internal.id, 'action');
                    }
                    else {
                        if (event.target && event.target.classList.contains('link')) {
                            internal.toast.handleClick(event);
                            this.dismiss(internal.id, 'action');
                        }
                    }
                }
            });
        }
        requestAnimationFrame(() => internal.el.classList.add('is-enter'));
        this.updateTimer(internal);
    }
    isAppKey(key) { return key === 'app'; }
    stackKey(scope) {
        return !scope ? 'app' : scope;
    }
    reflowStack(key) {
        const s = this.stacks.get(key);
        const max = this.isAppKey(key) ? this.maxVisibleApp : this.maxVisibleComponent;
        // sort visible by priority desc, createdAt desc
        s.list.sort((a, b) => (b.opts.priority ?? 0) - (a.opts.priority ?? 0) ||
            b.createdAt - a.createdAt);
        // enforce max visible
        while (s.list.length > max) {
            const moved = s.list.pop();
            this.pauseTimer(moved);
            moved.el.classList.add('is-exit');
            this.removeToast(moved);
            // moved.el.addEventListener('transitionend', () => moved.el.remove(), { once: true });
            s.queue.unshift(moved);
        }
        // pull from queue
        while (s.list.length < max && s.queue.length) {
            // priority first, then FIFO
            s.queue.sort((a, b) => (b.opts.priority ?? 0) - (a.opts.priority ?? 0));
            const next = s.queue.shift();
            s.list.unshift(next);
            s.container.appendChild(next.el);
            this.renderToast(next);
        }
    }
    updateToast(t) {
        t.toast.style = t.opts.style || 'info';
        t.toast.subject = (t.opts.subject ?? '');
        t.toast.message = (t.opts.message ?? '');
    }
    updateTimer(t) {
        // sticky
        const duration = t.opts.requireInteraction ? 0 : (t.opts.durationMs ?? t.remainingMs);
        if (!duration || duration <= 0)
            return;
        // reset then start
        this.resetTimer(t);
    }
    resetTimer(t) {
        if (t.timer)
            window.clearTimeout(t.timer);
        const total = t.opts.requireInteraction ? 0 : (t.opts.durationMs ?? 3500);
        t.remainingMs = total;
        if (total > 0) {
            const start = Date.now();
            t.timer = window.setTimeout(() => this.dismiss(t.id, 'timeout'), t.remainingMs);
            // keep remainingMs updated to support hover pause/resume
            const tick = () => {
                const elapsed = Date.now() - start;
                t.remainingMs = Math.max(0, total - elapsed);
                if (t.timer)
                    requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
        }
    }
    pauseTimer(t) {
        if (t.timer) {
            window.clearTimeout(t.timer);
            t.timer = undefined;
        }
    }
    resumeTimer(t) {
        if (t.opts.requireInteraction)
            return;
        if (t.opts.durationMs === 0)
            return;
        if (t.remainingMs <= 0) {
            this.dismiss(t.id, 'timeout');
            return;
        }
        if (!t.timer) {
            const start = Date.now();
            t.timer = window.setTimeout(() => this.dismiss(t.id, 'timeout'), t.remainingMs);
            const tick = () => {
                const elapsed = Date.now() - start;
                t.remainingMs = Math.max(0, t.remainingMs - elapsed);
                if (t.timer)
                    requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
        }
    }
    dismiss(id, reason) {
        for (const [key, s] of this.stacks) {
            const i = s.list.findIndex(t => t.id === id);
            if (i >= 0) {
                const [t] = s.list.splice(i, 1);
                this.pauseTimer(t);
                this.removeToast(t, reason);
                this.reflowStack(key);
                return;
            }
            const qi = s.queue.findIndex(t => t.id === id);
            if (qi >= 0) {
                const [t] = s.queue.splice(qi, 1);
                this.removeToast(t, reason);
                return;
            }
        }
    }
    removeToast(internal, reason) {
        const el = internal.el;
        let isRemoved = false;
        const done = () => {
            if (isRemoved)
                return;
            isRemoved = true;
            el.removeEventListener('transitionend', done);
            el.remove();
            if (reason) {
                internal.toast.destroy();
                internal.opts.onDismiss?.(reason, internal.toast);
            }
        };
        if (reason) {
            this.componentRemove(internal.id, false);
        }
        el.classList.add('is-exit');
        el.addEventListener('transitionend', done, { once: true });
        // fallback after 400ms
        window.setTimeout(done, 400);
    }
    template() {
        return '<div class="s360ToastStack" data-stack-key="app"></div>';
    }
    destroy() {
        super.destroy();
    }
}
ToastManager.VERSION = '$Revision: 111977 $';
